import React, { useEffect, useState } from 'react';
import { collection, doc, getDocs, query, where, getDoc } from 'firebase/firestore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';

import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

interface Propiedad {
  docId: string;
  id?: string;
  propiedad_id?: string;
  list_number?: string;
  listing_id?: string;
  titulo?: string;
  nombre?: string;
  precio_actual_usd?: number;
  precio_original_usd?: number;
  precio_actual_mxn?: number;
  estado?: string;
  estatus?: string;
  activo?: boolean;
  fuente?: string;
  inmobiliaria_doc_id?: string;
  agencia_nombre?: string;
  asesor_doc_ids?: string[];
  agencia_info?: {
    agencia_nombre?: string;
    agente_listador?: string;
    inmobiliaria_id?: string;
  };
  datos_flex?: {
    list_agent_name?: string;
    list_office_name?: string;
    rets_class?: string;
    mls_status?: string;
    modification_timestamp?: string;
    [key: string]: any;
  };
  tipo_propiedad?: string;
  zona?: string;
  recamaras?: number;
  banos_completos?: number;
  m2_total?: number;
  fecha_creacion?: any;
  [key: string]: any;
}

export default function AdminPropiedades() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filtroAsesorUid = searchParams.get('asesorUid');
  const [asesorInfo, setAsesorInfo] = useState<any>(null);
  const [propiedades, setPropiedades] = useState<Propiedad[]>([]);
  const [todasLasPropiedades, setTodasLasPropiedades] = useState<Propiedad[]>([]);
  const [filtro, setFiltro] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [filtroAgencia, setFiltroAgencia] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroZona, setFiltroZona] = useState('');
  const [filtroAsignacion, setFiltroAsignacion] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const [loading, setLoading] = useState(true);
  const propiedadesPorPagina = 15;

  useEffect(() => {
    const fetchPropiedades = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Cargar info del asesor si hay filtro por URL
        if (filtroAsesorUid) {
          try {
            const asesorDocSnap = await getDoc(doc(db, 'asesor', filtroAsesorUid));
            if (asesorDocSnap.exists()) {
              const asesorData = asesorDocSnap.data();
              setAsesorInfo({ id: asesorDocSnap.id, nombre: asesorData.nombre, asesor_id: asesorData.asesor_id });
            } else {
              setAsesorInfo(null);
            }
          } catch (err) {
            console.error('Error cargando asesor:', err);
            setAsesorInfo(null);
          }
        } else {
          setAsesorInfo(null);
        }

        // Cargar solo propiedades MLS activas
        const propiedadesRef = query(
          collection(db, 'propiedades'),
          where('fuente', '==', 'flexmls'),
          where('estado', '==', 'A')
        );
        const snapshot = await getDocs(propiedadesRef);

        // Cargar inmobiliarias en batch para evitar N+1 queries
        const inmobiliariaIdsArr: string[] = [];
        snapshot.docs.forEach(d => {
          const inmId = d.data().inmobiliaria_doc_id || d.data().inmobiliaria_id;
          if (inmId && !inmobiliariaIdsArr.includes(inmId)) inmobiliariaIdsArr.push(inmId);
        });

        const inmobiliariaMap = new Map<string, string>();
        for (const inmId of inmobiliariaIdsArr) {
          try {
            const inmSnap = await getDoc(doc(db, 'inmobiliaria', inmId));
            if (inmSnap.exists()) {
              inmobiliariaMap.set(inmId, inmSnap.data().nombre || 'Sin nombre');
            }
          } catch {}
        }

        const todasPropiedades: Propiedad[] = snapshot.docs.map(docSnap => {
          const data = docSnap.data();
          const inmId = data.inmobiliaria_doc_id || data.inmobiliaria_id;
          return {
            docId: docSnap.id,
            ...data,
            agencia_nombre: inmobiliariaMap.get(inmId) || data.agencia_info?.agencia_nombre || 'Sin agencia'
          } as Propiedad;
        });

        // Ordenar por fecha (mas recientes primero)
        todasPropiedades.sort((a, b) => {
          const fechaA = a.fecha_creacion?.seconds || 0;
          const fechaB = b.fecha_creacion?.seconds || 0;
          return fechaB - fechaA;
        });

        // Filtrar por permisos
        const esAdmin = user.tipo?.includes('admin');
        const propiedadesFiltradas = esAdmin
          ? todasPropiedades
          : todasPropiedades.filter(p => p.inmobiliaria_doc_id === user.inmobiliaria_doc_id);

        setTodasLasPropiedades(propiedadesFiltradas);
        setPropiedades(propiedadesFiltradas);
      } catch (error) {
        console.error('Error cargando propiedades:', error);
        console.error('No se pudieron cargar las propiedades.');
      } finally {
        setLoading(false);
      }
    };

    fetchPropiedades();
  }, [user, filtroAsesorUid]);

  // Aplicar filtros
  useEffect(() => {
    let resultado = todasLasPropiedades;

    // Filtro por asesor UID
    if (filtroAsesorUid && asesorInfo) {
      resultado = resultado.filter(p =>
        Array.isArray(p.asesor_doc_ids) && p.asesor_doc_ids.includes(asesorInfo.id)
      );
    }

    // Texto libre
    if (filtro.trim()) {
      const q = filtro.toLowerCase();
      resultado = resultado.filter(p => {
        const nombre = (p.titulo || p.nombre || '').toLowerCase();
        const listNum = (p.list_number || '').toLowerCase();
        const agente = (p.datos_flex?.list_agent_name || '').toLowerCase();
        const zona = (p.zona || '').toLowerCase();
        return nombre.includes(q) || listNum.includes(q) || agente.includes(q) || zona.includes(q);
      });
    }

    // Agencia
    if (filtroAgencia) {
      resultado = resultado.filter(p => p.agencia_nombre === filtroAgencia);
    }

    // Tipo de propiedad
    if (filtroTipo) {
      resultado = resultado.filter(p => p.tipo_propiedad === filtroTipo);
    }

    // Zona
    if (filtroZona) {
      resultado = resultado.filter(p => p.zona === filtroZona);
    }

    // Asignacion
    if (filtroAsignacion === 'sin_agente') {
      resultado = resultado.filter(p => !p.asesor_doc_ids || !Array.isArray(p.asesor_doc_ids) || p.asesor_doc_ids.length === 0);
    } else if (filtroAsignacion === 'sin_agencia') {
      resultado = resultado.filter(p => !p.inmobiliaria_doc_id && !p.inmobiliaria_id);
    } else if (filtroAsignacion === 'con_agente') {
      resultado = resultado.filter(p => Array.isArray(p.asesor_doc_ids) && p.asesor_doc_ids.length > 0);
    }

    // Estatus MLS
    if (filtroEstatus) {
      resultado = resultado.filter(p => (p.datos_flex?.mls_status || p.estado) === filtroEstatus);
    }

    setPropiedades(resultado);
    setPaginaActual(1);
  }, [filtro, filtroEstatus, filtroAgencia, filtroTipo, filtroZona, filtroAsignacion, todasLasPropiedades, filtroAsesorUid, asesorInfo]);

  if (!user || (!user.tipo?.includes('admin') && !user.tipo?.includes('asociado'))) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Acceso Denegado</h4>
          <p>No tienes permiso para acceder a esta seccion</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Cargando propiedades...</span>
          </div>
          <p className="mt-3">Cargando propiedades MLS...</p>
        </div>
      </div>
    );
  }

  const formatPrice = (propiedad: Propiedad) => {
    if (propiedad.precio_actual_usd && propiedad.precio_actual_usd > 0) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(propiedad.precio_actual_usd);
    } else if (propiedad.precio_original_usd && propiedad.precio_original_usd > 0) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(propiedad.precio_original_usd);
    }
    return 'No definido';
  };

  const getRetsClassLabel = (rets_class?: string) => {
    const map: Record<string, string> = { A: 'Depto', B: 'Casa', E: 'Terreno', F: 'Comercial', G: 'Negocio', H: 'Fraccion', I: 'Multi-Fam' };
    return map[rets_class || ''] || rets_class || '';
  };

  // Paginacion
  const totalPaginas = Math.ceil(propiedades.length / propiedadesPorPagina);
  const startIndex = (paginaActual - 1) * propiedadesPorPagina;
  const propiedadesPaginadas = propiedades.slice(startIndex, startIndex + propiedadesPorPagina);

  // Valores unicos para filtros
  const agenciasUnicas = Array.from(new Set(todasLasPropiedades.map(p => p.agencia_nombre).filter(Boolean))).sort();
  const tiposUnicos = Array.from(new Set(todasLasPropiedades.map(p => p.tipo_propiedad).filter(Boolean))).sort();
  const zonasUnicas = Array.from(new Set(todasLasPropiedades.map(p => p.zona).filter(Boolean))).sort();

  // Stats
  const statsSinAgente = todasLasPropiedades.filter(p => !p.asesor_doc_ids || !Array.isArray(p.asesor_doc_ids) || p.asesor_doc_ids.length === 0).length;
  const statsSinAgencia = todasLasPropiedades.filter(p => !p.inmobiliaria_doc_id && !p.inmobiliaria_id).length;

  return (
    <>
      <UserNav />
      <div className="container-fluid px-4 mt-4">
        <Breadcrumb
          items={
            filtroAsesorUid && asesorInfo
              ? [
                  { label: 'Dashboard', path: '/admin-dashboard' },
                  { label: 'Afiliados', path: '/admin-afiliado' },
                  { label: asesorInfo.nombre, path: `/ver-admin-afiliado/${filtroAsesorUid}` },
                  { label: 'Propiedades' }
                ]
              : [
                  { label: 'Dashboard', path: '/admin-dashboard' },
                  { label: 'Propiedades MLS' }
                ]
          }
        />

        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h2 className="mb-0">
              <i className="fas fa-home me-2 text-primary"></i>
              {filtroAsesorUid && asesorInfo
                ? `Propiedades de ${asesorInfo.nombre}`
                : 'Propiedades MLS'}
            </h2>
            <small className="text-muted">
              Fuente: FlexMLS (Vallarta-Nayarit) &mdash; Sync automatico cada 6h
            </small>
            {filtroAsesorUid && asesorInfo && (
              <div className="mt-1">
                <span className="badge bg-info">
                  <i className="fas fa-filter me-1"></i>
                  Filtrado por: {asesorInfo.nombre}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Stats compactos */}
        <div className="row g-2 mb-3">
          <div className="col-auto">
            <span className="badge bg-primary fs-6 p-2">
              <i className="fas fa-home me-1"></i>{todasLasPropiedades.length} MLS Activas
            </span>
          </div>
          <div className="col-auto">
            <span
              className={`badge fs-6 p-2 ${statsSinAgente > 0 ? 'bg-danger' : 'bg-success'}`}
              style={{ cursor: statsSinAgente > 0 ? 'pointer' : 'default' }}
              onClick={() => statsSinAgente > 0 && setFiltroAsignacion(filtroAsignacion === 'sin_agente' ? '' : 'sin_agente')}
            >
              <i className="fas fa-user-slash me-1"></i>{statsSinAgente} Sin agente
            </span>
          </div>
          <div className="col-auto">
            <span
              className={`badge fs-6 p-2 ${statsSinAgencia > 0 ? 'bg-warning text-dark' : 'bg-success'}`}
              style={{ cursor: statsSinAgencia > 0 ? 'pointer' : 'default' }}
              onClick={() => statsSinAgencia > 0 && setFiltroAsignacion(filtroAsignacion === 'sin_agencia' ? '' : 'sin_agencia')}
            >
              <i className="fas fa-building-circle-xmark me-1"></i>{statsSinAgencia} Sin agencia
            </span>
          </div>
          <div className="col-auto">
            <span className="badge bg-success fs-6 p-2">
              <i className="fas fa-check-circle me-1"></i>{todasLasPropiedades.filter(p => p.miembro_oficial).length} Oficiales
            </span>
          </div>
          <div className="col-auto">
            <span className="badge bg-secondary fs-6 p-2">
              <i className="fas fa-filter me-1"></i>{propiedades.length} mostradas
            </span>
          </div>
        </div>

        {/* Filtros */}
        <div className="card mb-3">
          <div className="card-body py-2">
            <div className="row g-2">
              <div className="col-md-3">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Buscar: nombre, MLS#, agente, zona..."
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filtroAgencia} onChange={(e) => setFiltroAgencia(e.target.value)}>
                  <option value="">Todas las agencias</option>
                  {agenciasUnicas.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                  <option value="">Todos los tipos</option>
                  {tiposUnicos.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filtroZona} onChange={(e) => setFiltroZona(e.target.value)}>
                  <option value="">Todas las zonas</option>
                  {zonasUnicas.map(z => <option key={z} value={z}>{z}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={filtroAsignacion} onChange={(e) => setFiltroAsignacion(e.target.value)}>
                  <option value="">Asignacion: Todas</option>
                  <option value="sin_agente">Sin agente</option>
                  <option value="sin_agencia">Sin agencia</option>
                  <option value="con_agente">Con agente</option>
                </select>
              </div>
              <div className="col-md-1">
                <button
                  className="btn btn-outline-secondary btn-sm w-100"
                  onClick={() => { setFiltro(''); setFiltroEstatus(''); setFiltroAgencia(''); setFiltroTipo(''); setFiltroZona(''); setFiltroAsignacion(''); }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla */}
        {propiedades.length === 0 ? (
          <div className="alert alert-info text-center">
            <i className="fas fa-info-circle me-2"></i>
            {filtroAsesorUid && asesorInfo
              ? `${asesorInfo.nombre} no tiene propiedades MLS asignadas.`
              : 'No hay propiedades que coincidan con los filtros.'}
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table aria-label="Tabla de Admin Property" className="table table-hover table-sm align-middle">
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: '30%' }}>Propiedad</th>
                    <th scope="col">Tipo</th>
                    <th scope="col">Zona</th>
                    <th scope="col">Agencia / Agente MLS</th>
                    <th scope="col">Precio</th>
                    <th scope="col">Specs</th>
                    <th style={{ width: '120px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {propiedadesPaginadas.map(p => {
                    const nombre = p.titulo || p.nombre || `MLS# ${p.list_number || 'Sin titulo'}`;
                    const agenteMls = p.datos_flex?.list_agent_name || p.agencia_info?.agente_listador || '';
                    const oficinaMls = p.agencia_nombre || p.datos_flex?.list_office_name || 'Sin agencia';
                    const retsClass = p.datos_flex?.rets_class;
                    const tieneAgente = Array.isArray(p.asesor_doc_ids) && p.asesor_doc_ids.length > 0;

                    return (
                      <tr key={p.docId}>
                        <td>
                          <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                            {nombre}
                          </div>
                          <div className="d-flex gap-1 mt-1 flex-wrap">
                            <span className="badge bg-primary bg-opacity-75" style={{ fontSize: '0.7rem' }}>
                              MLS# {p.list_number || p.listing_id || '?'}
                            </span>
                            {retsClass && (
                              <span className="badge bg-secondary" style={{ fontSize: '0.7rem' }}>
                                {getRetsClassLabel(retsClass)}
                              </span>
                            )}
                            {p.miembro_oficial ? (
                              <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>
                                <i className="fas fa-check-circle me-1"></i>Oficial
                              </span>
                            ) : (
                              <span className="badge bg-outline-secondary border" style={{ fontSize: '0.7rem', color: '#888' }}>
                                No oficial
                              </span>
                            )}
                            {!tieneAgente && (
                              <span className="badge bg-danger" style={{ fontSize: '0.7rem' }}>
                                <i className="fas fa-user-slash me-1"></i>Sin agente
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <small>{p.tipo_propiedad || '-'}</small>
                        </td>
                        <td>
                          <small>{p.zona || '-'}</small>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.85rem' }}>
                            <i className="fas fa-building me-1 text-muted"></i>
                            {oficinaMls}
                          </div>
                          {agenteMls && (
                            <small className="text-muted">
                              <i className="fas fa-user me-1"></i>{agenteMls}
                            </small>
                          )}
                        </td>
                        <td>
                          <span className="fw-bold text-success" style={{ fontSize: '0.9rem' }}>
                            {formatPrice(p)}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {p.recamaras ? `${p.recamaras}rec` : ''}
                            {p.banos_completos ? ` ${p.banos_completos}ba` : ''}
                            {p.m2_total ? ` ${Math.round(p.m2_total)}m2` : ''}
                          </small>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => navigate(`/view-admin-property/${p.docId}`)}
                            title="Ver detalle"
                          >
                            <i className="fas fa-eye me-1"></i>Ver
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginacion */}
            <div className="d-flex justify-content-between align-items-center mt-3 mb-4">
              <small className="text-muted">
                {startIndex + 1}-{Math.min(startIndex + propiedadesPorPagina, propiedades.length)} de {propiedades.length}
              </small>

              {totalPaginas > 1 && (
                <nav aria-label="Paginacion">
                  <ul className="pagination pagination-sm mb-0 flex-wrap">
                    <li className={`page-item ${paginaActual === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPaginaActual(paginaActual - 1)} disabled={paginaActual === 1}>
                        <i className="fas fa-chevron-left"></i>
                      </button>
                    </li>
                    {Array.from({ length: Math.min(7, totalPaginas) }, (_, i) => {
                      let pageNum: number;
                      if (totalPaginas <= 7) {
                        pageNum = i + 1;
                      } else if (paginaActual <= 4) {
                        pageNum = i + 1;
                      } else if (paginaActual >= totalPaginas - 3) {
                        pageNum = totalPaginas - 6 + i;
                      } else {
                        pageNum = paginaActual - 3 + i;
                      }
                      return (
                        <li key={pageNum} className={`page-item ${paginaActual === pageNum ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setPaginaActual(pageNum)}>{pageNum}</button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${paginaActual === totalPaginas ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPaginaActual(paginaActual + 1)} disabled={paginaActual === totalPaginas}>
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </>
        )}
      </div>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
